<script setup>
import { reactive, computed, watch, getCurrentInstance, ref } from 'vue';
import { Countries } from '/src/app/features/shared/libraries/countries.js';
import possiblePaymentProviders from '../raws/possiblePaymentProviders.json';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const sourceData = computed(
  () => $stores.appData?.state?.data?.paymentProviders || {}
);

// Get ALL country codes from possiblePaymentProviders (not from API)
const countryCodes = computed(() =>
  Object.keys(possiblePaymentProviders).filter(
    (code) => possiblePaymentProviders[code]
  )
);

const form = reactive({});
const expandedCategories = ref({});
const selectedCategory = ref('all');

// Helper function to format provider name for display
function formatProviderName(provider) {
  if (provider === 'none') return 'Bloquer';
  if (provider === 'manual') return 'Manuel';
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

// Helper to check if a specific field has changes
function hasFieldChange(countryCode, opType, operator) {
  return (
    form[countryCode]?.[opType]?.[operator] !==
    sourceData.value?.[countryCode]?.[opType]?.[operator]
  );
}

// Get available providers for a specific country, operation type and operator
function getAvailableProviders(countryCode, operationType, operator) {
  return (
    possiblePaymentProviders[countryCode]?.[operationType]?.[operator] || [
      'none',
      'manual'
    ]
  );
}

// Get all operation types for a country (from possiblePaymentProviders)
function getOperationTypes(countryCode) {
  const countryData = possiblePaymentProviders[countryCode];
  if (!countryData) return [];
  return Object.keys(countryData);
}

// Get all operators for a country and operation type (from possiblePaymentProviders)
function getOperators(countryCode, operationType) {
  const countryData = possiblePaymentProviders[countryCode];
  if (!countryData || !countryData[operationType]) return [];
  return Object.keys(countryData[operationType]);
}

// Operation categories
const operationCategories = {
  usdt: {
    id: 'usdt',
    label: 'Transactions USDT',
    icon: '💱',
    operations: ['buyUsdt', 'sellUsdt']
  },
  betting: {
    id: 'betting',
    label: 'Paris Sportifs',
    icon: '⚽',
    operations: [
      'fund1xBet',
      'withdrawal1xBet',
      'fundBetWinner',
      'withdrawalBetWinner',
      'fundMelbet',
      'withdrawalMelbet',
      'fund1Win',
      'withdrawal1Win',
      'fundEightStarz',
      'withdrawalEightStarz'
    ]
  },
  wallet: {
    id: 'wallet',
    label: 'Portefeuille',
    icon: '💰',
    operations: ['fundWallet', 'withdrawalWallet']
  },
  giftCard: {
    id: 'giftCard',
    label: 'Cartes Cadeaux',
    icon: '🎁',
    operations: ['buyGiftCard', 'sellGiftCard']
  },
  other: {
    id: 'other',
    label: 'Autres',
    icon: '📋',
    operations: [
      'platformSubscription',
      'subscription',
      'withdrawalAffiliation',
      'storeOperation'
    ]
  }
};

const categoryList = computed(() => Object.values(operationCategories));

// Get category for an operation
function getOperationCategory(opType) {
  for (const cat of categoryList.value) {
    if (cat.operations.includes(opType)) return cat;
  }
  return operationCategories.other;
}

// Get operations for selected country filtered by category
const filteredOperations = computed(() => {
  const code = selectedCountry.value;
  const allOps = getOperationTypes(code);

  if (selectedCategory.value === 'all') {
    return allOps;
  }

  const category = operationCategories[selectedCategory.value];
  return category
    ? allOps.filter((op) => category.operations.includes(op))
    : allOps;
});

// Get operations grouped by category for selected country
const operationsByCategory = computed(() => {
  const code = selectedCountry.value;
  const allOps = getOperationTypes(code);
  const grouped = {};

  categoryList.value.forEach((cat) => {
    grouped[cat.id] = allOps.filter((op) => cat.operations.includes(op));
  });

  return grouped;
});

// Initialize form for ALL countries in possiblePaymentProviders
watch(
  [sourceData, countryCodes],
  ([data]) => {
    // Initialize form for all countries from possiblePaymentProviders
    countryCodes.value.forEach((code) => {
      if (!form[code]) {
        form[code] = {};
        const operationTypes = getOperationTypes(code);
        operationTypes.forEach((opType) => {
          form[code][opType] = {};
          const operators = getOperators(code, opType);
          operators.forEach((operator) => {
            // Use API data if available, otherwise default to first available provider or 'none'
            const availableProviders = getAvailableProviders(
              code,
              opType,
              operator
            );
            const defaultValue = availableProviders.includes('none')
              ? 'none'
              : availableProviders[0] || 'none';
            form[code][opType][operator] =
              data?.[code]?.[opType]?.[operator] || defaultValue;
          });
        });
      } else {
        // Update existing form with API data
        const operationTypes = getOperationTypes(code);
        operationTypes.forEach((opType) => {
          if (!form[code][opType]) form[code][opType] = {};
          const operators = getOperators(code, opType);
          operators.forEach((operator) => {
            if (data?.[code]?.[opType]?.[operator] !== undefined) {
              form[code][opType][operator] = data[code][opType][operator];
            }
          });
        });
      }
    });
  },
  { immediate: true, deep: true }
);

// Declare selectedCountry FIRST
const selectedCountry = ref('');

// Initialize selectedCountry when countryCodes is available
watch(
  countryCodes,
  (codes) => {
    if (codes.length > 0 && !selectedCountry.value) {
      selectedCountry.value = codes[0];
    }
  },
  { immediate: true }
);

// Initialize expanded categories AFTER selectedCountry is declared
watch(
  selectedCountry,
  (newCountry) => {
    if (newCountry && !expandedCategories.value[newCountry]) {
      expandedCategories.value[newCountry] = {};
      categoryList.value.forEach((cat) => {
        expandedCategories.value[newCountry][cat.id] = false; // all collapsed by default
      });
    }
  },
  { immediate: true }
);

const hasChanges = computed(() => {
  return countryCodes.value.some((code) => {
    const operationTypes = getOperationTypes(code);
    return operationTypes.some((opType) => {
      const operators = getOperators(code, opType);
      return operators.some((operator) => {
        return (
          form[code]?.[opType]?.[operator] !==
          sourceData.value?.[code]?.[opType]?.[operator]
        );
      });
    });
  });
});

// Count changes for a country
const countryChangesCount = computed(() => (code) => {
  let count = 0;
  const operationTypes = getOperationTypes(code);
  operationTypes.forEach((opType) => {
    const operators = getOperators(code, opType);
    operators.forEach((operator) => {
      if (
        form[code]?.[opType]?.[operator] !==
        sourceData.value?.[code]?.[opType]?.[operator]
      ) {
        count++;
      }
    });
  });
  return count;
});

// Count changes for a category in a country
const categoryChangesCount = computed(() => (code, categoryId) => {
  let count = 0;
  const category = operationCategories[categoryId];
  if (!category) return 0;

  category.operations.forEach((opType) => {
    const operators = getOperators(code, opType);
    operators.forEach((operator) => {
      if (
        form[code]?.[opType]?.[operator] !==
        sourceData.value?.[code]?.[opType]?.[operator]
      ) {
        count++;
      }
    });
  });
  return count;
});

const selectedCountryHasChanges = computed(() => {
  const code = selectedCountry.value;
  return countryChangesCount.value(code) > 0;
});

const selectedCountryData = computed(() => {
  const country = Countries.find(selectedCountry.value);
  return {
    name: country?.countryName || selectedCountry.value,
    code: selectedCountry.value
  };
});

// Get operation types for selected country
const selectedCountryOperations = computed(() => {
  return getOperationTypes(selectedCountry.value);
});

// Toggle category expansion
function toggleCategory(categoryId) {
  const code = selectedCountry.value;
  if (expandedCategories.value[code]) {
    expandedCategories.value[code][categoryId] =
      !expandedCategories.value[code][categoryId];
  }
}

// Expand/collapse all categories
function expandAll() {
  const code = selectedCountry.value;
  categoryList.value.forEach((cat) => {
    if (expandedCategories.value[code]) {
      expandedCategories.value[code][cat.id] = true;
    }
  });
}

function collapseAll() {
  const code = selectedCountry.value;
  categoryList.value.forEach((cat) => {
    if (expandedCategories.value[code]) {
      expandedCategories.value[code][cat.id] = false;
    }
  });
}

function save() {
  if (!hasChanges.value) return;

  const payload = { paymentProviders: {} };

  countryCodes.value.forEach((code) => {
    payload.paymentProviders[code] = {};
    const operationTypes = getOperationTypes(code);
    operationTypes.forEach((opType) => {
      const operators = getOperators(code, opType);
      operators.forEach((operator) => {
        if (
          form[code]?.[opType]?.[operator] !==
          sourceData.value?.[code]?.[opType]?.[operator]
        ) {
          if (!payload.paymentProviders[code][opType]) {
            payload.paymentProviders[code][opType] = {};
          }
          payload.paymentProviders[code][opType][operator] =
            form[code][opType][operator];
        }
      });
    });
  });

  $bus.emit($constants.EVENTS.APP_DATA.UPDATE_PAYMENT_PROVIDERS, payload);
}

function reset() {
  countryCodes.value.forEach((code) => {
    const operationTypes = getOperationTypes(code);
    operationTypes.forEach((opType) => {
      const operators = getOperators(code, opType);
      operators.forEach((operator) => {
        const availableProviders = getAvailableProviders(
          code,
          opType,
          operator
        );
        const defaultValue = availableProviders.includes('none')
          ? 'none'
          : availableProviders[0] || 'none';
        form[code][opType][operator] =
          sourceData.value?.[code]?.[opType]?.[operator] || defaultValue;
      });
    });
  });
}

function resetCountry(code) {
  const operationTypes = getOperationTypes(code);
  operationTypes.forEach((opType) => {
    const operators = getOperators(code, opType);
    operators.forEach((operator) => {
      const availableProviders = getAvailableProviders(code, opType, operator);
      const defaultValue = availableProviders.includes('none')
        ? 'none'
        : availableProviders[0] || 'none';
      form[code][opType][operator] =
        sourceData.value?.[code]?.[opType]?.[operator] || defaultValue;
    });
  });
}

function resetCategory(code, categoryId) {
  const category = operationCategories[categoryId];
  if (!category) return;

  category.operations.forEach((opType) => {
    const operators = getOperators(code, opType);
    operators.forEach((operator) => {
      form[code][opType][operator] =
        sourceData.value?.[code]?.[opType]?.[operator] || 'none';
    });
  });
}

function resetOperation(code, opType) {
  const operators = getOperators(code, opType);
  operators.forEach((operator) => {
    const availableProviders = getAvailableProviders(code, opType, operator);
    const defaultValue = availableProviders.includes('none')
      ? 'none'
      : availableProviders[0] || 'none';
    form[code][opType][operator] =
      sourceData.value?.[code]?.[opType]?.[operator] || defaultValue;
  });
}

function operationHasChanges(code, opType) {
  const operators = getOperators(code, opType);
  return operators.some((operator) => {
    return (
      form[code]?.[opType]?.[operator] !==
      sourceData.value?.[code]?.[opType]?.[operator]
    );
  });
}

// Operation type labels for display
const operationLabels = {
  buyUsdt: 'Achat USDT',
  sellUsdt: 'Vente USDT',
  sellGiftCard: 'Vente Carte Cadeau',
  buyGiftCard: 'Achat Carte Cadeau',
  platformSubscription: 'Abonnements (Netflix, Spotify, ...)',
  fund1xBet: 'Dépot 1xBet',
  withdrawal1xBet: 'Retrait 1xBet',
  fundWallet: 'Dépot Portefeuille',
  withdrawalWallet: 'Retrait Portefeuille',
  fundBetWinner: 'Dépot BetWinner',
  withdrawalBetWinner: 'Retrait BetWinner',
  fundMelbet: 'Dépot Melbet',
  withdrawalMelbet: 'Retrait Melbet',
  fundEightStarz: 'Dépot 8 Starz',
  withdrawalEightStarz: 'Retrait 8 Starz',
  withdrawalAffiliation: 'Retrait Parrainage',
  fund1Win: 'Dépot 1Win',
  withdrawal1Win: 'Retrait 1Win',
  subscription: 'Abonnement Vip',
  storeOperation: 'Opération Boutique'
};
</script>

<template>
  <div class="space-y-6">
    <!-- HEADER -->
    <div class="flex justify-between items-center">
      <h2 class="text-xl font-semibold">Fournisseurs de paiement par pays</h2>
      <div class="flex gap-3" v-show="hasChanges">
        <button
          class="px-6 py-2 rounded-xl bg-gray-300 text-gray-700"
          @click="reset"
        >
          Annuler tout
        </button>
        <button
          class="px-6 py-2 rounded-xl bg-primary-600 text-white"
          @click="save"
        >
          Sauvegarder
        </button>
      </div>
    </div>

    <!-- NO DATA MESSAGE -->
    <div
      v-if="countryCodes.length === 0"
      class="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center"
    >
      <p class="text-yellow-800">
        Aucune configuration de paiement disponible. Vérifiez le fichier
        possiblePaymentProviders.json
      </p>
    </div>

    <!-- NO COUNTRY SELECTED MESSAGE -->
    <div
      v-else-if="!selectedCountry"
      class="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center"
    >
      <p class="text-gray-600">
        Sélectionnez un pays pour voir les configurations
      </p>
    </div>

    <template v-else>
      <!-- COUNTRY SELECTOR -->
      <div class="flex flex-wrap gap-2">
        <button
          v-for="code in countryCodes"
          :key="code"
          @click="selectedCountry = code"
          :class="[
            'px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2',
            selectedCountry === code
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          ]"
        >
          {{ Countries.find(code)?.countryName || code }}
          <span
            v-if="countryChangesCount(code) > 0"
            class="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full"
          >
            {{ countryChangesCount(code) }}
          </span>
        </button>
      </div>

      <!-- CATEGORY FILTER -->
      <div class="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
        <button
          @click="selectedCategory = 'all'"
          :class="[
            'px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2',
            selectedCategory === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          ]"
        >
          📋 Tout
        </button>
        <button
          v-for="cat in categoryList"
          :key="cat.id"
          @click="selectedCategory = cat.id"
          :class="[
            'px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2',
            selectedCategory === cat.id
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          ]"
        >
          {{ cat.icon }} {{ cat.label }}
        </button>
        <div class="ml-auto flex gap-2">
          <button
            @click="expandAll"
            class="px-3 py-2 text-sm rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            Tout déplier
          </button>
          <button
            @click="collapseAll"
            class="px-3 py-2 text-sm rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            Tout plier
          </button>
        </div>
      </div>

      <!-- COUNTRY DETAILS -->
      <div class="bg-slate-100 rounded-3xl p-6">
        <div
          class="flex justify-between items-center mb-6 pb-3 border-b border-gray-300"
        >
          <h3 class="text-lg font-semibold">
            {{ selectedCountryData.name }} ({{ selectedCountryData.code }})
          </h3>
          <button
            v-if="selectedCountryHasChanges"
            class="px-4 py-2 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
            @click="resetCountry(selectedCountry)"
          >
            Annuler pays
          </button>
        </div>

        <!-- CATEGORIES ACCORDIONS -->
        <div class="space-y-4">
          <div
            v-for="cat in categoryList"
            :key="cat.id"
            v-show="selectedCategory === 'all' || selectedCategory === cat.id"
            class="bg-white rounded-2xl shadow-sm overflow-hidden"
          >
            <!-- CATEGORY HEADER -->
            <div
              @click="toggleCategory(cat.id)"
              class="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              :class="{
                'border-b border-gray-200':
                  !expandedCategories[selectedCountry]?.[cat.id]
              }"
            >
              <div class="flex items-center gap-3">
                <span class="text-2xl">{{ cat.icon }}</span>
                <div>
                  <h4 class="font-semibold text-gray-800">{{ cat.label }}</h4>
                  <p class="text-sm text-gray-500">
                    {{ operationsByCategory[cat.id]?.length || 0 }} opération(s)
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-3">
                <span
                  v-if="categoryChangesCount(selectedCountry, cat.id) > 0"
                  class="px-3 py-1 bg-orange-100 text-orange-600 text-sm font-medium rounded-full"
                >
                  {{ categoryChangesCount(selectedCountry, cat.id) }} modifié(s)
                </span>
                <button
                  v-if="categoryChangesCount(selectedCountry, cat.id) > 0"
                  @click.stop="resetCategory(selectedCountry, cat.id)"
                  class="px-3 py-1 text-sm rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
                >
                  Annuler
                </button>
                <svg
                  class="w-5 h-5 text-gray-400 transition-transform"
                  :class="{
                    'rotate-180': expandedCategories[selectedCountry]?.[cat.id]
                  }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            <!-- CATEGORY CONTENT -->
            <div
              v-show="expandedCategories[selectedCountry]?.[cat.id]"
              class="p-4 space-y-4"
            >
              <div
                v-for="opType in operationsByCategory[cat.id]"
                :key="opType"
                class="bg-gray-50 rounded-xl p-4"
              >
                <div class="flex justify-between items-center mb-3">
                  <h5 class="font-medium text-gray-700">
                    {{ operationLabels[opType] || opType }}
                  </h5>
                  <button
                    v-if="operationHasChanges(selectedCountry, opType)"
                    @click="resetOperation(selectedCountry, opType)"
                    class="text-xs px-3 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                </div>
                <div
                  class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
                >
                  <div
                    v-for="operator in getOperators(selectedCountry, opType)"
                    :key="operator"
                  >
                    <label
                      class="block mb-1 text-sm font-medium text-gray-600"
                      >{{ operator }}</label
                    >
                    <select
                      class="inputComponent w-full"
                      :class="{
                        'border-orange-400 bg-orange-50': hasFieldChange(
                          selectedCountry,
                          opType,
                          operator
                        )
                      }"
                      v-model="form[selectedCountry][opType][operator]"
                    >
                      <option
                        v-for="provider in getAvailableProviders(
                          selectedCountry,
                          opType,
                          operator
                        )"
                        :key="provider"
                        :value="provider"
                      >
                        {{ formatProviderName(provider) }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
